import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  Stack,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@altimateai/lego";
import { CopyButton } from "../../components/copyButton";
import { CodeSQLEditor, SQLEditorRef } from "../../components/codeEditor";
import styles from "./styles.module.scss";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Feedback } from "../../components/feedback";
import { GET_QUERY_BY_ID } from "./constants";
import { InsightSection, QuerySqlLineage } from "./components";
import { RelativeComponentLoader } from "../../components/loader";
import { getQueryById, getQuerySqlLineageById } from "@/helpers/apis";
import * as Sentry from "@sentry/browser";
import { formatNumber } from "@/uiCore";
import dayjs from "dayjs";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { ColorInsightTags } from "@/components/tags";
import { useAppState } from "@/modules/app/useAppContext";
import { BackButton } from "@/components/button";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import QueryInsightsChatbot from "./components/queryInsightsChatbot";
import { useBreadcrumb } from "@/components/breadcrumbs";
import { GetQuerySQLLineageResponse } from "@/pages/explorer/types";
import { QueryDetailsItem } from "@/pages/query/types";
import { toSqlLineageProps } from "@/pages/query/components/QuerySqlLineage";
import { useFilteredOpportunities } from "@/modules/insights/useFilteredOpportunities";
import { QueriesTracking } from "@/lib/tracking/queries";

interface MainContentProps {
  queryId: string;
  query: QueryDetailsItem;
  selectedInsight: string | undefined;
  sqlLineage: GetQuerySQLLineageResponse | undefined;
  setSelectedNode: (node: string) => void;
  parentEditorRef: MutableRefObject<SQLEditorRef | null>;
  showSideContent: string | null | false;
  from: string | null;
  startDate: Date;
  endDate: Date;
  onDateRangeChange: (startDate: Date, endDate: Date) => void;
}

function MainContent({
  queryId,
  query,
  selectedInsight,
  sqlLineage,
  setSelectedNode,
  parentEditorRef,
  showSideContent,
  from,
  startDate,
  endDate,
  onDateRangeChange,
}: MainContentProps) {
  const { currency } = useAppState();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const opportunityId = searchParams.get("opportunityId");
  const setOpportunityId = (v: string) => updateSearchParamsByKey({ opportunityId: v });

  // Get active tab from query param, default to "code"
  const activeTab = searchParams.get("tab") || "code";

  useEffect(() => {
    if (activeTab === "sql-visualizer") {
      QueriesTracking.lineageViewed(queryId);
    }
  }, [activeTab, queryId]);

  // Helper function to generate search params with tab value
  const getSearchWithTab = (tab: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);
    return params.toString();
  };

  useEffect(() => {
    if (!opportunityId) return;
    const tagsMeta = sqlLineage?.query_tags_meta;
    for (const key in tagsMeta ?? {}) {
      for (const t of tagsMeta?.[key] ?? []) {
        if (opportunityId === t.id) {
          setSelectedNode(key);
          document.dispatchEvent(new CustomEvent("onSelectedNodes", { detail: key }));
          return;
        }
      }
    }
  }, []);

  // Determine if SQL Visualizer tab should be shown
  const showSqlVisualizerTab = sqlLineage && sqlLineage.is_lineage;

  const queryTags = (query.tags ?? []).flatMap(t => {
    if (t !== null && typeof t === "object" && "tag_rk" in t) {
      const tagRk = (t as Record<string, unknown>)["tag_rk"];
      return typeof tagRk === "string" ? [tagRk] : [];
    }
    return [];
  });

  const filteredOpportunities = useFilteredOpportunities(sqlLineage?.tags ?? []);

  const tagsMeta = sqlLineage?.query_tags_meta;

  return (
    <div className={styles.query_details}>
      <Card>
        <CardContent className="al-pt-4">
          <Stack direction="column" gap="md">
            <Stack align="center" gap="sm">
              <BackButton defaultUrl={from || `/query/all`} />
              <div className="fs-4">Query ID:</div>
              <h1 className="fs-4 text-primary">{queryId}</h1>
              <CopyButton value={queryId} />
              <div className="spacer" />
              <QueryInsightsChatbot queryId={queryId} instanceId={query.instance_id} />
            </Stack>
            <div className={styles.query_metrics_container}>
              {[
                {
                  label: "Est. Cost",
                  value: formatNumber(query.cost, { currency }),
                },
                {
                  label: "Exec. time",
                  value: query.execution_time,
                },
                {
                  label: "Partitions Scanned",
                  value: query.percent_partition_scanned,
                },
                {
                  label: "Query Type",
                  value: query.query_type,
                },
                {
                  label: "Warehouse",
                  value: query.warehouse_name,
                },
                {
                  label: "Bytes spilled",
                  value: query.bytes_spilled,
                },
                {
                  label: "User",
                  value: query.user_name,
                },
                {
                  label: "Timestamp",
                  value: query.timestamp,
                },
                {
                  label: "Cache fetched",
                  value: query.percentage_scanned_from_cache,
                },
                {
                  label: "Resource Utilization",
                  value: query.query_load_percent,
                },
                {
                  label: "Warehouse Size",
                  value: query.warehouse_size,
                },
                {
                  label: "Query Hash",
                  value: <Link to={`/query/groups/${query.query_hash}`}>{query.query_hash}</Link>,
                },
              ].map(({ label, value }) => (
                <Stack key={label}>
                  <div className="fw-semibold me-1" aria-label={`${label} label`}>
                    {label}:
                  </div>
                  <div className="text-muted" aria-label={`${label} value`}>
                    {value}
                  </div>
                </Stack>
              ))}
            </div>
            <ColorInsightTags tags={queryTags} />
            <Tabs value={activeTab} className="al-w-full" variant={"underlined"}>
              <TabsList>
                {showSqlVisualizerTab && (
                  <TabsTrigger value="sql-visualizer" asChild>
                    <Link to={{ search: getSearchWithTab("sql-visualizer") }}>SQL Visualizer</Link>
                  </TabsTrigger>
                )}
                <TabsTrigger value="code" asChild>
                  <Link to={{ search: getSearchWithTab("code") }}>Code</Link>
                </TabsTrigger>
              </TabsList>
              {showSqlVisualizerTab && (
                <TabsContent value="sql-visualizer">
                  <QuerySqlLineage
                    sqlLineage={sqlLineage ? toSqlLineageProps(sqlLineage) : null}
                    selectedInsight={selectedInsight ?? null}
                    showSideContent={Boolean(showSideContent)}
                    onNodeSelect={(node: string) => {
                      setSelectedNode(node);
                      const nodeTagsMeta = tagsMeta?.[node] ?? [];
                      const _opportunityId = nodeTagsMeta[0]?.opportunity_id ?? "";
                      setOpportunityId(_opportunityId);
                    }}
                  />
                </TabsContent>
              )}
              <TabsContent value="code">
                <CodeSQLEditor
                  ref={parentEditorRef}
                  value={query.query_text}
                  height="40vh"
                  lineNumbers={true}
                  showCopy={true}
                  minimap
                />
              </TabsContent>
            </Tabs>
          </Stack>
        </CardContent>
      </Card>
      <div className={"al-bg-white al-p-3 al-my-2 al-rounded-md"}>
        <InsightSection
          queryId={queryId}
          opportunities={filteredOpportunities}
          sqlLineage={sqlLineage}
          queryText={query.query_text}
          totalRuns={query.query_hash_count != null ? String(query.query_hash_count) : undefined}
          selectedOpportunityId={opportunityId}
          setOpportunityId={(item: string) => {
            QueriesTracking.opportunitySelected(queryId, item);
            setOpportunityId(item);
            if (!sqlLineage) return;
            parentEditorRef.current?.onParentResize();
            const insightId = sqlLineage.tags.find(t => t.opportunity_id === item)?.id;
            const _selectNode = (key: string) => {
              setSelectedNode(key);
              document.dispatchEvent(new CustomEvent("onSelectedNodes", { detail: key }));
            };
            if (!insightId) return _selectNode("");
            for (const key in tagsMeta) {
              for (const t of tagsMeta[key] ?? []) {
                if (insightId === t.id) return _selectNode(key);
              }
            }
            _selectNode("");
          }}
          startDate={startDate}
          endDate={endDate}
          onDateRangeChange={onDateRangeChange}
          showMultiStatementError={query?.query_type === "MULTI_STATEMENT"}
          showActionsDropdown={true}
          showTimeSavings
        />
      </div>
      <Feedback
        feedback_id={queryId}
        feedback_type="query_tag"
        resetWorkflow={() => {}}
        afterSubmit={() => {}}
        className=""
      />
    </div>
  );
}

const QueryDetails = () => {
  const { queryId } = useParams();
  const editorRef = useRef<SQLEditorRef>(null);
  const [selectedNode, setSelectedNode] = useState("");
  const { searchParams } = useFiltersBySearchParams();

  useBreadcrumb(queryId ?? "Query", {
    defaultParents: [
      { label: "Code", pathname: "/query" },
      { label: "All Queries", pathname: "/query/all" },
    ],
  });

  useEffect(() => {
    if (queryId) {
      QueriesTracking.detailViewed(queryId);
    }
  }, [queryId]);

  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(28, "day").toDate(),
    dayjs().toDate()
  );

  const {
    data: querydetails,
    isLoading,
    error,
  } = useQuery({
    queryKey: [GET_QUERY_BY_ID, queryId, startDate, endDate],
    queryFn: () =>
      getQueryById(queryId!, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() ?? "",
      }),
    enabled: Boolean(queryId),
  });

  const { data: sqlLineage, isLoading: isSqlLineageLoading } = useQuery({
    queryKey: ["GET_QUERY_SQL_LINEAGE_BY_ID", queryId, startDate, endDate],
    queryFn: () =>
      getQuerySqlLineageById(queryId!, {
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() ?? "",
      }),
    enabled: Boolean(queryId),
    meta: {
      onError: (err: unknown) => {
        if (err === "canceled") {
          Sentry.captureException(new Error("SQL Lineage timeout"));
        }
      },
    },
  });

  const opportunityId = searchParams.get("opportunityId");
  const selectedInsight = (sqlLineage?.tags ?? []).find(t => t.id === opportunityId)?.tag_rk;

  if (isLoading || isSqlLineageLoading) {
    return (
      <div className="al-h-full align-content-center">
        <RelativeComponentLoader label="Analyzing query might take a few seconds" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{String(error)}</AlertDescription>
      </Alert>
    );
  }

  if (!querydetails) {
    return (
      <Alert variant="destructive">
        <AlertDescription>No data found for query</AlertDescription>
      </Alert>
    );
  }

  const showSideContent = selectedNode || opportunityId;
  const { query } = querydetails;

  return (
    <MainContent
      queryId={queryId ?? ""}
      parentEditorRef={editorRef}
      selectedInsight={selectedInsight}
      query={query}
      setSelectedNode={setSelectedNode}
      sqlLineage={sqlLineage}
      showSideContent={showSideContent}
      from={searchParams.get("from")}
      startDate={startDate}
      endDate={endDate}
      onDateRangeChange={onUpdate}
    />
  );
};

export { QueryDetails };
